// import { Component } from '@theme/component';
// import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
// import { MegaMenuHoverEvent } from '@theme/events';

// /**
//  * A custom element that manages a header menu.
//  *
//  * @typedef {Object} State
//  * @property {HTMLElement | null} activeItem - The currently active menu item.
//  *
//  * @typedef {object} Refs
//  * @property {HTMLElement} overflowMenu - The overflow menu.
//  * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
//  *
//  * @extends {Component<Refs>}
//  */
// class HeaderMenu extends Component {
//   requiredRefs = ['overflowMenu'];

//   /**
//    * @type {MutationObserver | null}
//    */
//   #submenuMutationObserver = null;

//   connectedCallback() {
//     super.connectedCallback();

//     this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
//     // on load, cache the max height of the submenu so you can use it in a translate
//     this.#cacheMaxOverflowMenuHeight();

//     onDocumentLoaded(this.#preloadImages);
//     window.addEventListener('resize', this.#resizeListener);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     window.removeEventListener('resize', this.#resizeListener);
//     this.#cleanupMutationObserver();
//   }

//   /**
//    * Debounced resize event listener to recalculate menu style
//    */
//   #resizeListener = debounce(() => {
//     this.#cacheMaxOverflowMenuHeight();
//     setHeaderMenuStyle();
//   }, 100);

//   /**
//    * @type {State}
//    */
//   #state = {
//     activeItem: null,
//   };

//   /**
//    * Get the overflow menu
//    */
//   get overflowMenu() {
//     return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
//   }

//   /**
//    * Whether the overflow menu is hovered
//    * @returns {boolean}
//    */
//   get overflowHovered() {
//     return this.refs.overflowMenu?.matches(':hover') ?? false;
//   }

//   get headerComponent() {
//     return /** @type {HTMLElement | null} */ (this.closest('header-component'));
//   }

//   /**
//    * Activate the selected menu item immediately
//    * @param {PointerEvent | FocusEvent} event
//    */
//   activate = (event) => {
//     this.dispatchEvent(new MegaMenuHoverEvent());

//     if (!(event.target instanceof Element)) return;

//     let item = findMenuItem(event.target);

//     if (!item || item == this.#state.activeItem) return;

//     const isDefaultSlot = event.target.slot === '';

//     this.dataset.overflowExpanded = (!isDefaultSlot).toString();

//     const previouslyActiveItem = this.#state.activeItem;

//     if (previouslyActiveItem) {
//       previouslyActiveItem.ariaExpanded = 'false';
//     }

//     this.#state.activeItem = item;
//     this.ariaExpanded = 'true';
//     item.ariaExpanded = 'true';

//     let submenu = findSubmenu(item);

//     if (!submenu && !isDefaultSlot) {
//       submenu = this.overflowMenu;
//     }

//     if (submenu) {
//       // Mark submenu as active for content-visibility optimization
//       submenu.dataset.active = '';

//       // Cleanup any existing mutation observer from previous menu activations
//       this.#cleanupMutationObserver();

//       // Monitor DOM mutations to catch deferred content injection (from section hydration)
//       this.#submenuMutationObserver = new MutationObserver(() => {
//         requestAnimationFrame(() => {
//           // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
//           requestAnimationFrame(() => {
//             if (submenu.offsetHeight > 0) {
//               this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
//               this.#cleanupMutationObserver();
//             }
//           });
//         });
//       });
//       this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

//       // Auto-disconnect after 500ms to prevent memory leaks
//       setTimeout(() => {
//         this.#cleanupMutationObserver();
//       }, 500);
//     }

//     const submenuHeight = submenu ? submenu.offsetHeight : 0;

//     this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
//     this.style.setProperty('--submenu-opacity', '1');
//   };

//   /**
//    * Deactivate the active item after a delay
//    * @param {PointerEvent | FocusEvent} event
//    */
//   deactivate(event) {
//     if (!(event.target instanceof Element)) return;

//     const menu = findSubmenu(this.#state.activeItem);
//     const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
//     const isMovingToSubmenu =
//       event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
//     const isMovingToOverflowMenu =
//       event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

//     if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

//     this.#deactivate();
//   }

//   /**
//    * Deactivate the active item immediately
//    * @param {HTMLElement | null} [item]
//    */
//   #deactivate = (item = this.#state.activeItem) => {
//     if (!item || item != this.#state.activeItem) return;
//     if (this.overflowHovered) return;

//     this.headerComponent?.style.setProperty('--submenu-height', '0px');
//     this.style.setProperty('--submenu-opacity', '0');
//     this.dataset.overflowExpanded = 'false';

//     const submenu = findSubmenu(item);

//     this.#state.activeItem = null;
//     this.ariaExpanded = 'false';
//     item.ariaExpanded = 'false';

//     // Remove active state from submenu after animation completes
//     if (submenu) {
//       delete submenu.dataset.active;
//     }
//   };

//   /**
//    * Preload images that are set to load lazily.
//    */
//   #preloadImages = () => {
//     const images = this.querySelectorAll('img[loading="lazy"]');
//     images?.forEach((image) => image.removeAttribute('loading'));
//   };

//   /**
//    * Caches the maximum height of all submenus for consistent animations
//    * Stores the value in a CSS custom property for use in transitions
//    */
//   #cacheMaxOverflowMenuHeight() {
//     const submenus = this.querySelectorAll('[ref="submenu[]"]');
//     const maxHeight = Math.max(
//       ...Array.from(submenus)
//         .filter((submenu) => submenu instanceof HTMLElement)
//         .map((submenu) => submenu.offsetHeight)
//     );
//     this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
//   }

//   #cleanupMutationObserver() {
//     this.#submenuMutationObserver?.disconnect();
//     this.#submenuMutationObserver = null;
//   }
// }

// if (!customElements.get('header-menu')) {
//   customElements.define('header-menu', HeaderMenu);
// }

// /**
//  * Find the closest menu item.
//  * @param {Element | null | undefined} element
//  * @returns {HTMLElement | null}
//  */
// function findMenuItem(element) {
//   if (!(element instanceof Element)) return null;

//   if (element?.matches('[slot="more"')) {
//     // Select the first overflowing menu item when hovering over the "More" item
//     return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
//   }

//   return element?.querySelector('[ref="menuitem"]');
// }

// /**
//  * Find the closest submenu.
//  * @param {Element | null | undefined} element
//  * @returns {HTMLElement | null}
//  */
// function findSubmenu(element) {
//   const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
//   return submenu instanceof HTMLElement ? submenu : null;
// }
import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // `pointerleave` on shadow DOM parts is unreliable in Firefox — it may
    // never fire when the pointer leaves a [part="overflow"] element inside
    // a shadow root. Fix: listen on both the shadow part (Chrome/Safari) AND
    // the host custom element with `mouseleave` (Firefox fallback), and also
    // on `this` (the header-menu host) so leaving the whole menu area works.
    this.overflowMenu?.addEventListener('pointerleave', this.#onMenuLeave);
    this.overflowMenu?.addEventListener('mouseleave', this.#onMenuLeave);
    this.refs.overflowMenu?.addEventListener('mouseleave', this.#onMenuLeave);

    // Also deactivate when pointer leaves the header-menu host element itself
    this.addEventListener('mouseleave', this.#onHostLeave);

    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.overflowMenu?.removeEventListener('pointerleave', this.#onMenuLeave);
    this.overflowMenu?.removeEventListener('mouseleave', this.#onMenuLeave);
    this.refs.overflowMenu?.removeEventListener('mouseleave', this.#onMenuLeave);
    this.removeEventListener('mouseleave', this.#onHostLeave);

    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered.
   * Uses `matches(':hover')` on the host ref (not the shadow part) so
   * Firefox can correctly evaluate the hover state.
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Fired when the pointer/mouse leaves the overflow menu (shadow part or host).
   * Using a named arrow so it can be added and removed by reference.
   */
  #onMenuLeave = () => {
    this.#deactivate();
  };

  /**
   * Fired when the pointer/mouse leaves the header-menu host element entirely.
   * Guards against the relatedTarget still being inside this component.
   * @param {MouseEvent} event
   */
  #onHostLeave = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#deactivate();
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated
          // and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Handle keydown events to close the active menu on Escape.
   * Listens at the document level so it fires regardless of where
   * focus is — on a nav item, inside a submenu link, or anywhere else.
   * Bypasses overflowHovered so the menu always closes instantly.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const item = this.#state.activeItem;
    const submenu = findSubmenu(item);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }

    // Return focus to the trigger nav item so keyboard users aren't lost
    item.closest('[slot]')?.querySelector('a, button')?.focus();
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}



  function appendPromoImages() {
    const promoImages = document.querySelectorAll('.promo-images');
    const megaMenus = document.querySelectorAll('.mega-menu__list');

    const menuMap = {};

    megaMenus.forEach(menu => {
      const title = menu.getAttribute('data-title');
      if (title) {
        menuMap[title] = menu;
      }
    });

    promoImages.forEach(promo => {
      const target = promo.getAttribute('data-target-link');
      if (!target || !menuMap[target]) return;

      if (!menuMap[target].contains(promo)) {
        menuMap[target].appendChild(promo);
      }
    });
  }

  appendPromoImages();

  const observer = new MutationObserver(function (mutations) {
    appendPromoImages();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });


